import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { ChevronDown, Check, Plus, Briefcase, HardHat } from "lucide-react";

// Venture switcher (Jun 2026): one account, up to 5 businesses/projects.
export const VentureSwitcher = ({ onSwitched }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    api.get("/companies/mine").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  if (!data || (data.ventures.length <= 1 && data.ventures.length >= data.max)) return null;

  const switchTo = async (id, kind) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/companies/switch", { company_id: id });
      // Projects and businesses live on different flows — land on the right one.
      const path = window.location.pathname;
      const onProjectPage = path.startsWith("/project/");
      if (kind === "project" && !onProjectPage) {
        window.location.href = "/project/intake";
      } else if (kind !== "project" && onProjectPage) {
        window.location.href = "/founder/dashboard";
      } else {
        onSwitched ? onSwitched() : window.location.reload();
      }
    } catch (_) { setBusy(false); }
  };

  const addVenture = async (kind) => {
    const noun = kind === "project" ? "project" : "business";
    const name = window.prompt(`Name of your new ${noun}?`);
    if (!name || !name.trim()) return;
    setBusy(true);
    try {
      await api.post("/companies", { company_name: name.trim(), kind });
      if (kind === "project") { window.location.href = "/project/intake"; return; }
      onSwitched ? onSwitched() : window.location.reload();
    } catch (e) {
      window.alert(e?.response?.data?.detail || `Couldn't add the ${noun}`);
      setBusy(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="venture-switcher-btn"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded-lg bg-[#0A8B85] px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#066667]"
      >
        My ventures <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div
          data-testid="venture-switcher-menu"
          className="absolute left-0 top-full z-40 mt-1.5 w-72 rounded-xl border border-neutral-200 bg-white p-1.5 shadow-xl"
        >
          {data.ventures.map((v) => (
            <button
              key={v.company_id}
              data-testid={`venture-item-${v.company_id}`}
              onClick={() => switchTo(v.company_id, v.kind)}
              disabled={busy}
              className={"flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-neutral-50 " +
                (v.is_active ? "bg-[#0A8B85]/5" : "")}
            >
              {v.kind === "project"
                ? <HardHat className="h-4 w-4 flex-shrink-0 text-amber-600" />
                : <Briefcase className="h-4 w-4 flex-shrink-0 text-[#0A8B85]" />}
              <span className="min-w-0 flex-1 truncate font-medium text-neutral-800">
                {v.company_name || "Unnamed venture"}
              </span>
              <span className="text-[10px] uppercase tracking-wide text-neutral-400">
                {v.kind}
              </span>
              {v.is_active && <Check className="h-4 w-4 text-[#0A8B85]" />}
            </button>
          ))}
          {data.ventures.length < data.max && (
            <>
              <button
                data-testid="venture-add-business-btn"
                onClick={() => addVenture("business")}
                disabled={busy}
                className="mt-1 flex w-full items-center gap-2 rounded-lg border-t border-neutral-100 px-2.5 py-2 pt-2.5 text-left text-sm font-semibold text-[#066667] hover:bg-[#0A8B85]/5"
              >
                <Plus className="h-4 w-4" /> Add another business
              </button>
              <button
                data-testid="venture-add-project-btn"
                onClick={() => addVenture("project")}
                disabled={busy}
                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-semibold text-amber-700 hover:bg-amber-50"
              >
                <HardHat className="h-4 w-4" /> Add a project
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
